import { usePromisedRequest } from "../Common/request";
import { resolveNoContent, resolveWithBody } from "./Utils/utils";
import { translate } from "../Common/translations";
import { Bundle } from "../Common/consts";

export type CreateFederationRequest = {
    name: string;
    country: string;
    billingDetails: BillingDetails;
};

export type CreateFederationResponse = {
    federationId: string;
};

export type FederationRole = "owner" | "fullOrganizer" | "technician" | "staff";

export const FederationRoleEnum: Record<string, FederationRole> = {
    Owner: "owner",
    FullOrganizer: "fullOrganizer",
    Technician: "technician",
    Staff: "staff",
} as const;

export function translateFederationRole(role: FederationRole): string {
    return translate("federation_role." + role);
}

export type UserFederation = {
    id: string;
    federationName: string;
    federationRole: FederationRole;
    logoUrl: string | null;
    approved: boolean;
};

export type FullFederationMetadata = {
    id: string;
    federationName: string;
    federationCountry: string;
    logoUrl: string | null;
};

export type UpdateFederationMetadataRequest = {
    federationName: string;
    federationCountry: string;
    logoUrl: string | null;
};

export type UploadedFederationLogo = {
    url: string;
};

export type FederationMemberData = {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    role: FederationRole;
    bundles: Bundle[];
};

export type InviteMemberRequest = {
    federationId: string;
    email: string;
    role: FederationRole;
    bundles: Bundle[];
};

export type UpdateFederationMemberRequest = {
    federationId: string;
    userId: number;
    role: FederationRole;
    bundles: Bundle[];
};

export type MigrateToFederationRequest = {
    eventId: number;
    federationId: string;
};

export type FederationRankingSummary = {
    id: number;
    name: string;
    created: string;
};

export type FederationEventWithRankings = {
    eventId: number;
    eventName: string;
    eventDate: string | null;
    eventEndDate: string | null;
    eventTimezone: string;
    rankings: FederationRankingSummary[];
};

export type BillingDetails = {
    companyName: string | null;
    street: string | null;
    city: string | null;
    postalCode: string | null;
    country: string | null;
    taxId: string | null;
};

export type BillingDetailsResponse = {
    billingDetails: BillingDetails | null;
};

export type PostWizardOnboardingDecision = "started_tour" | "skipped" | "completed_tour";

export type PostWizardOnboardingDecisionRequest = {
    federationId: string;
    decision: PostWizardOnboardingDecision;
};

type PostWizardOnboardingDecisionRequestBody = {
    decision: PostWizardOnboardingDecision;
};

export type UpdateBillingDetailsRequest = {
    federationId: string;
    billingDetails: BillingDetails;
};

export default {
    createNewFederation(request: CreateFederationRequest): Promise<CreateFederationResponse> {
        return usePromisedRequest<CreateFederationResponse>({
            url: "/api/organizers/federations",
            method: "post",
            json: request,
        }).then(resolveWithBody());
    },
    getFederationListingForUser(): Promise<UserFederation[]> {
        return usePromisedRequest<UserFederation[]>({
            url: `/api/organizers/federations`,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },
    getFederationMetadata(federationId: string): Promise<FullFederationMetadata> {
        return usePromisedRequest<FullFederationMetadata>({
            url: `/api/organizers/federations/${federationId}`,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },
    updateFederationMetadata(federationId: string, request: UpdateFederationMetadataRequest): Promise<void> {
        return usePromisedRequest<void>({
            url: `/api/organizers/federations/${federationId}`,
            method: "put",
            json: request,
        }).then(resolveNoContent());
    },
    recordPostWizardOnboardingDecision(request: PostWizardOnboardingDecisionRequest): Promise<void> {
        const body: PostWizardOnboardingDecisionRequestBody = { decision: request.decision };

        return usePromisedRequest<void>({
            url: `/api/admin/federations/${request.federationId}/actions/onboarding-decision`,
            method: "post",
            json: body,
        }).then(resolveNoContent());
    },
    uploadFederationLogoImage(federationId: string, file: File): Promise<UploadedFederationLogo> {
        const formData = new FormData();
        formData.append("file", file, file.name);

        return usePromisedRequest<UploadedFederationLogo>({
            url: `/api/organizers/federations/${federationId}/logo`,
            method: "post",
            body: formData,
            headers: {
                Accept: "application/json",
            },
        }).then(resolveWithBody());
    },

    // MEMBERS

    getFederationMembersListing(federationId: string): Promise<FederationMemberData[]> {
        return usePromisedRequest<FederationMemberData[]>({
            url: `/api/organizers/federations/${federationId}/members`,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },
    inviteMemberToFederation(request: InviteMemberRequest): Promise<void> {
        return usePromisedRequest<void>({
            url: `/api/organizers/federations/${request.federationId}/members`,
            method: "post",
            json: request,
        }).then(resolveNoContent());
    },
    updateFederationMember(request: UpdateFederationMemberRequest): Promise<void> {
        return usePromisedRequest<void>({
            url: `/api/organizers/federations/${request.federationId}/members/${request.userId}`,
            method: "put",
            json: request,
        }).then(resolveNoContent());
    },
    removeFederationMember(federationId: string, userId: number): Promise<void> {
        return usePromisedRequest<void>({
            url: `/api/organizers/federations/${federationId}/members/${userId}`,
            method: "delete",
        }).then(resolveNoContent());
    },

    //MIGRATION OF EVENT TO FEDERATION

    migrateEventToFederation(request: MigrateToFederationRequest): Promise<void> {
        return usePromisedRequest<void>({
            url: `/api/organizers/events/${request.eventId}/actions/migrate-to-federation/${request.federationId}`,
            method: "put",
            json: request,
        }).then(resolveNoContent());
    },

    // FEDERATION RANKINGS

    getFederationRankingsEvents(federationId: string): Promise<FederationEventWithRankings[]> {
        return usePromisedRequest<FederationEventWithRankings[]>({
            url: `/api/organizers/federations/${federationId}/rankings/events`,
            json: true,
            method: "get",
        }).then(resolveWithBody());
    },

    mergeFederationRankings(federationId: string, rankingIds: number[]): Promise<string> {
        return usePromisedRequest<string>({
            url: `/api/organizers/federations/${federationId}/rankings/merge`,
            method: "post",
            json: { rankingIds },
        }).then(resolveWithBody());
    },

    // BILLING DETAILS

    getBillingDetails(federationId: string): Promise<BillingDetailsResponse> {
        return usePromisedRequest<BillingDetailsResponse>({
            url: `/api/organizers/federations/${federationId}/billing-details`,
            method: "get",
            json: true,
        }).then(resolveWithBody());
    },
    updateBillingDetails(request: UpdateBillingDetailsRequest): Promise<void> {
        return usePromisedRequest<void>({
            url: `/api/organizers/federations/${request.federationId}/billing-details`,
            method: "put",
            json: request,
        }).then(resolveNoContent());
    },
};
